import { useMemo, useState } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { UseBaseTable } from 'hooks/useBaseTable';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useQueryState } from 'hooks/useQueryState';

import { useMutationExtractDelayedActivitiesCsv } from 'api/mutations/extractDelayedActivitiesCsv.mutation';
import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryGetTopOverrunActivities } from 'api/queries/getTopDurationOverrunRisk.query';

import {
    FilterNames,
    filterMapper,
    getFiltersData,
} from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

import {
    TDelayTypeOptions,
    TOP_OVERDUE_OVERRUN_FILTERS,
    delayTypeStatusInitialState,
    statusFilterInitialState,
} from './DelayedActivities.utils';

export type UseDelayedActivitiesProvider = ReturnType<typeof useDelayedActivitiesProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    contractId?: number | null | undefined;
    projectId?: number | null | undefined;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    table: UseBaseTable['table'];
    widgetViewMode?: WidgetViewMode;
}

export const useDelayedActivitiesProvider = ({
    componentKey,
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
    table,
    widgetViewMode,
}: Props) => {
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);

    //Widget local state
    const [statusFilter, setStatusFilter] = useState<readonly ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.statusFilter : statusFilterInitialState,
    );
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.isCriticalPath : false,
    );
    const [delayTypeStatus, setDelayTypeStatus] = useState<ISelectOption<TDelayTypeOptions[]> | null>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.delayTypeStatus
            : delayTypeStatusInitialState,
    );
    const [status, setStatus] = useState<readonly ISelectOption<string>[]>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ? localFilters?.status : undefined,
    );
    const [nextFilter, setNextFilter] = useQueryState('nextFilter', widgetId ? undefined : '-1');

    const [numOfMonths, setNumOfMonths] = useState<ISelectOption<string> | undefined>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode)
            ? localFilters?.numOfMonths
            : TOP_OVERDUE_OVERRUN_FILTERS.find((filter) => filter.value === nextFilter),
    );

    const source = isInPinboard
        ? localFilters
        : { ...globalFilterData, statusFilter, isCriticalPath, status, delayTypeStatus, nextFilter, numOfMonths };

    const optionType = useMemo(() => {
        const statusOptions = source?.status?.map((item) => item.value)?.map(filterMapper) ?? [];
        const delayTypeStatusOptions = source?.delayTypeStatus?.value?.map(filterMapper) ?? [];

        return [...statusOptions, ...delayTypeStatusOptions, ...(source?.isCriticalPath ? ['CRITICAL_PATH'] : [])];
    }, [source?.status, source?.delayTypeStatus, source?.isCriticalPath]);

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags
                ?.filter((item) => !item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                ?.filter((item) => item.value.isExclude)
                .map((i) => i.value.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
            [FilterNames.OPTIONS_STATUS]: source?.statusFilter?.map((item) => item.value)?.map(filterMapper),
            [FilterNames.OPTIONS_TYPE]: optionType,
            [FilterNames.RESOURCE_NAME_CARD_BOARD]: source?.resource,
            [FilterNames.RESOURCE_TYPE_CARD_BOARD]: source?.resourceType,
        });
    }, [source?.activityCodes, source?.milestoneFragnet, source?.tags, source?.wbs, source?.statusFilter, optionType]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        resource: source?.resource,
        resourceType: source?.resourceType,
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        statusFilter: source?.statusFilter?.map((item) => item.label),
        isCriticalPath: source?.isCriticalPath,
        status: source?.status?.map((item) => item.label),
        delayTypeStatus: source?.delayTypeStatus?.label,
        numOfMonths: source?.numOfMonths?.label,
    };
    const { pillsList: pillsFromHook } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });
    const pills = useMemo(() => pillsFromHook, [JSON.stringify(pillsFromHook)]);

    //queries
    const getTopOverrunActivitiesPayload = {
        id: [latestVersionId],
        comparedVersionId: compareVersionId,
        filter: {
            filters,
        },
        sortingRequestList: table.sorting,
        paging: { ...table.pagination, page: table.pagination.page - 1 },
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
    };

    //mutations
    const csvExportMutationPayload = {
        id: [latestVersionId],
        comparedVersionId: compareVersionId,
        filter: {
            filters,
        },
    };

    return {
        ui: {
            state: getState({ compareVersionId, contractId, latestVersionId, projectId, source, table }),
            pills,
            setters: {
                setStatusFilter,
                setIsCriticalPath,
                setStatus,
                setDelayTypeStatus,
                setNextFilter,
                setNumOfMonths,
            },
            isInPinboard,
        },
        api: {
            getTopOverrunActivities: {
                instance: useQueryGetTopOverrunActivities(getTopOverrunActivitiesPayload),
                payload: getTopOverrunActivitiesPayload,
            },
            extractDelayedActivitiesToCsv: {
                instance: useMutationExtractDelayedActivitiesCsv(),
                payload: csvExportMutationPayload,
            },
            setWidgetColumns: {
                instance: useMutationSetWidgetColumns(),
            },
        },
    };
};

const getState = ({ source, table, projectId, contractId, latestVersionId, compareVersionId }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,

        // globalFilter
        progressMeasurement: source?.progressMeasurement,
        resource: source?.resource,
        resourceType: source?.resourceType,
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        // table
        sorting: table.sorting,
        columnOrder: table.columnOrder,

        // local
        statusFilter: source?.statusFilter,
        isCriticalPath: source?.isCriticalPath,
        status: source?.status,
        delayTypeStatus: source?.delayTypeStatus,
        numOfMonths: source?.numOfMonths,
        nextFilter: source?.nextFilter,
    };
};
